import { Injectable, inject } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { HippoUtilsService } from '@hippo/services/utils';
import { environment } from 'environments/environment';
import { firstValueFrom, lastValueFrom, Observable } from 'rxjs';
import { AuthService } from './auth.service';
import { UserService } from '../services/user.service';

@Injectable({ providedIn: 'root' })
export class CognitoAuthService {
    private readonly oidc = inject(OidcSecurityService);
    private readonly authService = inject(AuthService);
    private readonly userService = inject(UserService);

    private sessionReady: Promise<boolean> | null = null;

    readonly isAuthenticated$ = this.oidc.isAuthenticated$;
    readonly userData$ = this.oidc.userData$;

    login(): void {
        this.oidc.authorize();
    }

    checkAuth(): Observable<unknown> {
        return this.oidc.checkAuth();
    }

    getAccessToken(): Observable<string> {
        return this.oidc.getAccessToken();
    }

    /**
     * Memoized session handshake. Translation/data loads await this so they
     * never hit the PHP backend before the Cognito token has been exchanged
     * for a logged-in PHP session (the backend answers 401 otherwise).
     */
    ensureSession(): Promise<boolean> {
        if (!this.sessionReady) {
            this.sessionReady = this.handleRedirectCallback().catch(error => {
                console.error('Cognito session handshake failed', error);
                return false;
            });
        }
        return this.sessionReady;
    }

    /**
     * Returns true if a Cognito session was just exchanged for a PHP session.
     * Caller is responsible for navigating after this resolves.
     */
    async handleRedirectCallback(): Promise<boolean> {
        const result: any = await firstValueFrom(this.oidc.checkAuth());
        if (!result?.isAuthenticated || !result?.idToken) {
            return false;
        }
        if (
            this.userService.getUser()?.uid &&
            HippoUtilsService.getCookie('PHPSESSID')
        ) {
            return true;
        }
        await this.authService.getPhpSessionCookie(true);
        const response: any = await lastValueFrom(
            this.authService.signInWithCognitoToken(result.idToken)
        );
        const user = response?.result;
        if (!user?.uid) {
            return false;
        }
        sessionStorage.setItem('user', JSON.stringify(user));
        this.userService.getUser();
        return true;
    }

    logout(): void {
        if (typeof window === 'undefined') {
            return;
        }
        try {
            window.sessionStorage.clear();
        } catch {}
        const { hostedUiDomain, clientId, postLogoutRedirectUri } =
            environment.cognito;
        const logoutUri = encodeURIComponent(postLogoutRedirectUri);
        window.location.href = `${hostedUiDomain}/logout?client_id=${clientId}&logout_uri=${logoutUri}`;
    }
}
